'use client';

import * as Accordion from '@radix-ui/react-accordion';
import classNames from 'classnames';
import { LucideChevronRight, LucideList } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AccordionTableOfContents } from './AccordionTableOfContents/AccordionTableOfContents';
import type { Heading } from './types';

import styles from './TableOfContentsMobile.module.scss';

interface Props {
    className?: string;
    contained?: boolean; // used when ToC is sticky inside of container
    headings: Heading[];
}

const SECTION_NAME = 'toc';

export function TableOfContentsMobile({ className, headings, contained }: Props) {
    const [value, setValue] = useState('');
    const isOpen = value === SECTION_NAME;

    const handleClose = () => {
        setValue('');
    };

    useEffect(() => {
        if (contained) {
            return undefined;
        }

        document.body.classList.add(styles.bodyExtraScroll);

        return () => {
            document.body.classList.remove(styles.bodyExtraScroll);
        };
    }, [contained]);

    return (
        <>
            <Accordion.Root
                type="single"
                className={classNames(styles.wrapper, className, {
                    [styles.open]: isOpen,
                    [styles.contained]: contained,
                })}
                collapsible
                value={value}
                onValueChange={setValue}
            >
                <Accordion.Item value={SECTION_NAME}>
                    <Accordion.Content className={styles.content}>
                        <div className={styles.listWrapper}>
                            <AccordionTableOfContents
                                expand
                                headings={headings}
                                onItemSelected={handleClose}
                            />
                        </div>
                    </Accordion.Content>
                    <Accordion.Header asChild>
                        <Accordion.Trigger className={styles.heading}>
                            <LucideList className={styles.listIcon} />
                            Table of contents
                            <LucideChevronRight className={styles.expandIcon} />
                        </Accordion.Trigger>
                    </Accordion.Header>
                </Accordion.Item>
            </Accordion.Root>

            <div
                aria-label="Close"
                className={classNames(styles.backdrop, { [styles.visible]: isOpen })}
                role="button"
                tabIndex={0}
                onClick={handleClose}
                onKeyDown={handleClose}
            />
        </>
    );
}
